import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type JSX } from 'react';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { EntityTable, PillButton, type EntityTableColumn } from '@/components/entity';
import { ErrorState, Skeleton } from '@/components/feedback';
import {
  deactivateAvailabilityAlertSubscriberForUser,
  listAvailabilityAlertSubscribersForUser,
  reactivateAvailabilityAlertSubscriberForUser,
} from '@/lib/services/availabilityAlerts';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import type { AvailabilityAlertSubscriberRow } from '@/lib/supabase/availabilityAlerts';

const formatTimestamp = (value: string | null): string => {
  if (value === null || value.length === 0) return '--';
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(parsed));
};

export function AvailabilityAlertsSubscriberTable(): JSX.Element {
  const user = useDashboardUser();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const subscribersQuery = useQuery({
    queryKey: ['availability-alert-subscribers'],
    queryFn: async ({ signal }) => {
      return await listAvailabilityAlertSubscribersForUser({ user, signal });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async (subscriberId: string) => {
      return await deactivateAvailabilityAlertSubscriberForUser({ user, subscriberId });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['availability-alert-subscribers'] });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: async (subscriberId: string) => {
      return await reactivateAvailabilityAlertSubscriberForUser({ user, subscriberId });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['availability-alert-subscribers'] });
    },
  });

  const subscribers = useMemo(() => subscribersQuery.data ?? [], [subscribersQuery.data]);
  const isLoading = subscribersQuery.isLoading;
  const filteredSubscribers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle.length === 0) return subscribers;
    return subscribers.filter((subscriber) => subscriber.email.includes(needle));
  }, [search, subscribers]);

  const columns = useMemo<ReadonlyArray<EntityTableColumn<AvailabilityAlertSubscriberRow>>>(() => {
    const busy = deactivateMutation.isPending || reactivateMutation.isPending;
    return [
      {
        id: 'email',
        header: 'Email',
        renderCell: (subscriber) => (
          <span className="font-medium text-text-primary">{subscriber.email}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        renderCell: (subscriber) => (
          <StatusBadge
            label={subscriber.is_active ? 'Active' : 'Removed'}
            tone={subscriber.is_active ? 'success' : 'danger'}
            secondaryLabel={subscriber.source}
            secondaryTone="neutral"
          />
        ),
      },
      {
        id: 'updated',
        header: 'Updated',
        renderCell: (subscriber) => (
          <span className="text-xs text-text-primary/70">
            {formatTimestamp(subscriber.updated_at)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        align: 'right',
        renderCell: (subscriber) =>
          subscriber.is_active ? (
            <PillButton
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => {
                deactivateMutation.mutate(subscriber.id);
              }}
            >
              Remove
            </PillButton>
          ) : (
            <PillButton
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => {
                reactivateMutation.mutate(subscriber.id);
              }}
            >
              Restore
            </PillButton>
          ),
      },
    ];
  }, [deactivateMutation, reactivateMutation]);

  if (subscribersQuery.isError) {
    return (
      <ErrorState
        title="Unable to load subscribers"
        description="Check your connection and try again."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <label className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
        Search
        <input
          type="text"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
          className="mt-1 w-full rounded-xl border border-surface-subtle bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
          placeholder="Filter by email"
        />
      </label>

      {isLoading ? (
        <div className="flex flex-col gap-2" aria-label="Loading subscribers">
          <Skeleton className="h-10 w-full rounded-xl" />
          <Skeleton className="h-10 w-full rounded-xl" />
          <Skeleton className="h-10 w-full rounded-xl" />
        </div>
      ) : (
        <EntityTable
          items={filteredSubscribers}
          columns={columns}
          getRowKey={(subscriber) => subscriber.id}
          emptyState={
            <p className="rounded-2xl border border-dashed border-surface-subtle bg-surface-subtle/40 p-6 text-center text-sm text-text-primary/70">
              No subscribers yet.
            </p>
          }
        />
      )}
    </div>
  );
}
